import React from 'react';
import { Button, Tag, Typography } from 'antd';
import { PlusOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * The parts the vendor suggested for a line — a list, not an order.
 *
 * The owner reviewed real generated estimates and found the lookup adding every
 * part it could find straight onto the ticket, with no way to take one back off.
 * So this component does two things and refuses a third:
 *
 *   - it splits the suggestions into **Needed** (the procedure requires it) and
 *     **Recommended** (worth doing while in there — the shop's call);
 *   - it gives every row an Add control, and a row already on the line reads as
 *     added and offers Remove;
 *   - it never puts anything on the ticket by itself.
 *
 * It owns no state. The parts already on the line arrive as a prop from the
 * estimate or work-order screen that is already holding them — fetching them
 * here would race the screen that is editing them.
 *
 * Mobile is the primary environment: rows wrap at 390px and the control drops
 * to the end of the row rather than squeezing the part name.
 */

export interface ShopNotePart {
  name: string;
  partNumber?: string | null;
  priceUsd?: number | null;
  why?: string;
  /**
   * Whether the procedure requires this part.
   *
   * Three spellings are in flight, and rows written before any of them existed
   * have none. `need` is what the lookup actually writes today; the other two
   * are earlier shapes still present in stored rows. Missing reads as Needed: a
   * required part quietly filed under "optional" is how a truck ends up apart
   * with the wrong box on the bench.
   */
  need?: 'needed' | 'recommended';
  needed?: boolean;
  kind?: 'needed' | 'recommended';
  /** How many the procedure calls for — six plugs on a V6, not one. */
  quantity?: number | null;
}

/** A part already on the line. Only the fields used for matching are required. */
export interface LinePartRef {
  id?: number | string;
  part_number?: string | null;
  description?: string | null;
}

export interface SuggestedPartsListProps {
  parts: ShopNotePart[];
  /** The line's current parts, so a suggestion can show as already added. */
  partsOnLine?: LinePartRef[];
  /** Called with the whole suggestion. Omit to render the list read-only. */
  onAdd?: (part: ShopNotePart) => void;
  /** Called with the suggestion and the line part it matched. */
  onRemove?: (part: ShopNotePart, onLine: LinePartRef) => void;
}

/** True only when the data says so — anything unmarked is Needed. */
export const isRecommended = (p: ShopNotePart): boolean =>
  p.need === 'recommended' || p.kind === 'recommended' || p.needed === false;

/** Part numbers are printed with dashes, spaces and case nobody agrees on. */
const normNumber = (v: unknown): string =>
  typeof v === 'string' ? v.replace(/[^a-z0-9]/gi, '').toUpperCase() : '';

const normName = (v: unknown): string =>
  typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().toLowerCase() : '';

/**
 * The line part this suggestion is already on the ticket as, if any.
 *
 * When both sides carry a part number the number decides — two different plugs
 * share the description "Spark Plug", and treating them as the same part hides
 * one the job needs. Otherwise fall back to the description, because the vendor
 * does not number every consumable and the advisor still types a name for it.
 */
export function findOnLine(
  part: ShopNotePart,
  partsOnLine: LinePartRef[] = []
): LinePartRef | undefined {
  const num = normNumber(part.partNumber);
  const name = normName(part.name);
  return partsOnLine.find((lp) => {
    const lpNum = normNumber(lp.part_number);
    if (num && lpNum) return num === lpNum;
    return !!name && normName(lp.description) === name;
  });
}

const PartRow: React.FC<{
  part: ShopNotePart;
  onLine?: LinePartRef;
  onAdd?: (p: ShopNotePart) => void;
  onRemove?: (p: ShopNotePart, onLine: LinePartRef) => void;
}> = ({ part, onLine, onAdd, onRemove }) => {
  // Never invent a price. The vendor leaves consumables unpriced and stores that as
  // null or 0; a rendered "$0.00" reads as free and goes out on the estimate.
  const price =
    typeof part.priceUsd === 'number' && part.priceUsd > 0 ? `$${part.priceUsd.toFixed(2)}` : null;
  const qty = typeof part.quantity === 'number' && part.quantity > 0 ? part.quantity : null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        flexWrap: 'wrap',
        padding: '6px 0',
        borderBottom: '1px solid #f3f4f6',
      }}
    >
      {/* minWidth 0 lets a long part name wrap instead of pushing the control
          off the right edge of a 390px phone. */}
      <div style={{ flex: '1 1 180px', minWidth: 0 }}>
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          {part.partNumber ? (
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
              {part.partNumber}
            </Tag>
          ) : null}
          <Text style={{ fontSize: 12 }}>{part.name}</Text>
          {qty ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Qty {qty}
            </Text>
          ) : null}
          {price ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {price}
            </Text>
          ) : null}
          {onLine ? (
            <Tag color="green" icon={<CheckOutlined />} style={{ marginInlineEnd: 0 }}>
              Added
            </Tag>
          ) : null}
        </span>
        {part.why ? (
          <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>{part.why}</div>
        ) : null}
      </div>

      <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
        {onLine
          ? onRemove && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                aria-label={`Remove ${part.name}`}
                onClick={() => onRemove(part, onLine)}
              >
                Remove
              </Button>
            )
          : onAdd && (
              <Button
                size="small"
                type="primary"
                ghost
                icon={<PlusOutlined />}
                aria-label={`Add ${part.name}`}
                onClick={() => onAdd(part)}
              >
                Add
              </Button>
            )}
      </div>
    </div>
  );
};

const SuggestedPartsList: React.FC<SuggestedPartsListProps> = ({
  parts,
  partsOnLine,
  onAdd,
  onRemove,
}) => {
  if (!Array.isArray(parts) || parts.length === 0) return null;

  const needed = parts.filter((p) => !isRecommended(p));
  const recommended = parts.filter(isRecommended);

  // The section carries its heading as an aria-label so the two lists are
  // distinguishable to a screen reader — and so a row can be asserted to have
  // landed in the right one.
  const section = (heading: string, hint: string, rows: ShopNotePart[]) =>
    rows.length === 0 ? null : (
      <section aria-label={heading} style={{ marginTop: 8 }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>
          {heading} ({rows.length})
        </h4>
        <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 2 }}>{hint}</div>
        {rows.map((p, i) => (
          <PartRow
            key={`${p.partNumber ?? p.name}-${i}`}
            part={p}
            onLine={findOnLine(p, partsOnLine)}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        ))}
      </section>
    );

  return (
    <div>
      {section('Needed', 'The procedure requires these.', needed)}
      {section('Recommended', 'Worth doing while in there — your call.', recommended)}
    </div>
  );
};

export default SuggestedPartsList;
